import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, HStack, Image, rem, StyleProps, VStack } from '@bonusly/design-system';
import { useRecognizeURL } from '@bonusly/url';
import { useFeatureFlag, Variant } from '@bonusly/who-am-i';

import logo from '../../../assets/images/bonusly-logo.png';
import { NAVBAR_HEIGHT_IN_PX } from '../../../config/layout';
import { CompanyLogo } from '../../../modules/user-navbar/components/company-logo';
import { SpoofingWarning } from '../../developer/spoofing-warning';
import { Reveal } from '../../display/reveal';
import { AppLayout } from '../../layout/AppLayout';
import { useSpoofing } from '../../layout/hooks/useSpoofing';
import { Unauthorized } from './Unauthorized';

interface NavbarShellProps extends StyleProps {
	children?: ReactNode;
	logo: ReactNode;
}

export const NavbarShell: FC<NavbarShellProps> = ({ children, logo, ...props }) => {
	return (
		<Flex alignItems="center" as="nav" h={rem(NAVBAR_HEIGHT_IN_PX)} id="primary-nav" px={{ base: 4, lg: 6 }} w="full" {...props}>
			<Flex w="full" alignItems="center">
				<Flex alignItems="center" flexBasis={0} flexGrow={1} h={rem(40)}>
					{/* use anchor instead of Link - this component renders outside router context as error boundary fallback */}
					<Box as="a" id="primary-nav-logo" h="full" href="/">
						{logo}
					</Box>
				</Flex>

				{children}
			</Flex>
		</Flex>
	);
};

export const NavFallback: FC = () => {
	const { t } = useTranslation('monarch');
	const isHomepageRedesignEnabled = useFeatureFlag(Variant.HomepageRedesign);
	const signInStartURL = useRecognizeURL('/users/sign_in_start');
	const { data } = useSpoofing();
	const isSpoofing = data?.is_spoofing && !data?.is_demoing;

	if (isHomepageRedesignEnabled) {
		return (
			<Flex direction="column" h="100vh">
				<Reveal if={isSpoofing}>
					<Box w="100%">
						<SpoofingWarning />
					</Box>
				</Reveal>
				<HStack flex="1" minH="0">
					<VStack bgColor="bg.canvas" minW={rem(260)} h="100%" py={rem(20)} align="flex-start">
						{/* use anchor instead of Link - this component renders outside router context as error boundary fallback */}
						<Box as="a" alignItems="left" width="100%" h={rem(36)} px={rem(18)} href="/">
							<CompanyLogo />
						</Box>
					</VStack>
					<Unauthorized
						link={
							<Button as="a" href={signInStartURL}>
								{t('links.re_login')}
							</Button>
						}
					/>
				</HStack>
			</Flex>
		);
	}

	return (
		<AppLayout navbar={<NavbarShell logo={<Image alt={t('links.company_logo', { name: 'Bonusly' })} h={rem(40)} src={logo} />} />}>
			<Unauthorized
				link={
					<Button as="a" href={signInStartURL}>
						{t('links.re_login')}
					</Button>
				}
			/>
		</AppLayout>
	);
};
